import React, { useContext, useEffect, useState } from "react";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import { Link } from "react-router-dom";
import MenuButton from "../MenuButton/MenuButton";
import moment from "moment";


const NutritionDashboard = () => {
  const [loading, setLoading]           = useState(false);
  const [statistics, setStatistics]     = useState(null);
  const [dietStats, setDietStats]       = useState(null);
  const [campStats, setCampStats]       = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [calendarAppts, setCalendarAppts] = useState([]);
  const [weekStart, setWeekStart]       = useState(moment().startOf("isoWeek")); // Monday

  const { state } = useContext(NoteState);
  const { branch_id, role, UId, first_name, last_name } = state;

  // Week days derived from weekStart
  const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, "days"));

  useEffect(() => {
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCalendarAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const params = {
        nutritionist_id: role === "10" ? UId : null,
        branch_id,
      };

      const [apptRes, dietRes, campRes, refRes] = await Promise.allSettled([
        UserServices.getNutritionStatistics(params),
        UserServices.getNutritionDietPlanStats(params),
        UserServices.getNutritionHealthCampStats(params),
        UserServices.getNutritionReferralStats(params),
      ]);

      if (apptRes.status === "fulfilled" && apptRes.value.status === 200 && apptRes.value.data.status) {
        setStatistics(apptRes.value.data.data);
      }
      if (dietRes.status === "fulfilled" && dietRes.value.status === 200 && dietRes.value.data.status) {
        setDietStats(dietRes.value.data.data);
      }
      if (campRes.status === "fulfilled" && campRes.value.status === 200 && campRes.value.data.status) {
        setCampStats(campRes.value.data.data);
      }
      if (refRes.status === "fulfilled" && refRes.value.status === 200 && refRes.value.data.status) {
        setReferralStats(refRes.value.data.data);
      }
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const fetchCalendarAppointments = async () => {
    try {
      const response = await UserServices.getNutritionAppointments(200, 1, {
        nutritionist_id: role === "10" ? UId : null,
        branch_id,
        start_date: weekStart.format("YYYY-MM-DD"),
        end_date: weekStart.clone().endOf("isoWeek").format("YYYY-MM-DD"),
      });
      if (response.status === 200 && response.data.status) {
        const data = response.data.data;
        setCalendarAppts(Array.isArray(data) ? data : data.data || []);
      }
    } catch (err) {
      console.log(err);
      setCalendarAppts([]);
    }
  };

  const conversionColor = (conversion) => {
    if (!conversion) return "#6c757d";
    if (conversion === "Paid") return "#198754";
    if (["Gold", "Platinum"].includes(conversion)) return "#ffc107";
    if (conversion === "Silver") return "#0dcaf0";
    if (conversion === "Not Attended") return "#dc3545";
    return "#0d6efd";
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">

        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="page-heading">
              <h3>
                <i className="bi bi-heart-pulse-fill me-2"></i>Nutrition Dashboard
              </h3>
              <p className="text-muted mb-0">
                Welcome back, {first_name} {last_name}!
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="row g-3 mb-4">
              {[
                { label: "Total", value: statistics?.total_appointments || 0, icon: "bi-calendar-check", color: "primary" },
                { label: "Today", value: statistics?.today_appointments || 0, icon: "bi-calendar-day", color: "success" },
                { label: "Upcoming", value: statistics?.upcoming_appointments || 0, icon: "bi-clock-history", color: "info" },
                {
                  label: "Conversions",
                  value: statistics?.conversion_stats?.filter((c) =>
                    ["Paid","Gold","Silver","Platinum"].includes(c.conversion)
                  ).reduce((s, c) => s + (c.count || 0), 0) || 0,
                  icon: "bi-trophy",
                  color: "warning",
                },
              ].map((card, i) => (
                <div key={i} className="col-12 col-sm-6 col-xl-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center">
                      <div className="flex-grow-1">
                        <p className="mb-0 text-muted small">{card.label} Appointments</p>
                        <h4 className="mb-0 fw-bold">{card.value}</h4>
                      </div>
                      <div className={`widget-icon bg-${card.color} bg-gradient text-white rounded-3`}>
                        <i className={`bi ${card.icon} fs-4`}></i>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Secondary Stats — Diet Plans · Health Camps · Referrals */}
            <div className="row g-3 mb-4">
              {[
                {
                  label: "Diet Plans",
                  sublabel: "This Month",
                  value: dietStats?.this_month ?? "—",
                  total: dietStats?.total,
                  icon: "bi-journal-medical",
                  color: "teal",
                  hex: "#0d7a5f",
                  link: "/nutrition/diet-plans",
                },
                {
                  label: "Health Camps",
                  sublabel: "This Month",
                  value: campStats?.this_month ?? "—",
                  total: campStats?.total,
                  icon: "bi-hospital",
                  color: "indigo",
                  hex: "#6610f2",
                  link: "/nutrition/health-camps",
                },
                {
                  label: "Referrals",
                  sublabel: "This Week",
                  value: referralStats?.current_week?.referrals ?? "—",
                  total: referralStats?.total_referrals,
                  icon: "bi-person-plus-fill",
                  color: "orange",
                  hex: "#fd7e14",
                  link: "/nutrition/referrals",
                },
              ].map((card, i) => (
                <div key={i} className="col-12 col-sm-4">
                  <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${card.hex}` }}>
                    <div className="card-body d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 52, height: 52, background: `${card.hex}20` }}
                      >
                        <i className={`bi ${card.icon} fs-4`} style={{ color: card.hex }}></i>
                      </div>
                      <div className="flex-grow-1">
                        <p className="mb-0 text-muted small fw-semibold text-uppercase" style={{ letterSpacing: 0.5 }}>
                          {card.label}
                        </p>
                        <div className="d-flex align-items-baseline gap-2">
                          <h3 className="mb-0 fw-bold" style={{ color: card.hex }}>{card.value}</h3>
                          <small className="text-muted">{card.sublabel}</small>
                        </div>
                        {card.total !== undefined && (
                          <small className="text-muted">All time: {card.total}</small>
                        )}
                      </div>
                      <a href={card.link} className="btn btn-sm btn-outline-secondary rounded-3">
                        <i className="bi bi-arrow-right"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Weekly Calendar — Day-Card View */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-transparent border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="mb-0">
                  <i className="bi bi-calendar-week me-2"></i>
                  {weekStart.format("DD MMM")} {String.fromCharCode(8211)} {weekStart.clone().endOf("isoWeek").format("DD MMM YYYY")}
                </h5>
                <div className="btn-group btn-group-sm">
                  <button className="btn btn-outline-secondary" onClick={() => setWeekStart((w) => w.clone().subtract(1, "week"))}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  <button className="btn btn-outline-primary px-3" onClick={() => setWeekStart(moment().startOf("isoWeek"))}>
                    Today
                  </button>
                  <button className="btn btn-outline-secondary" onClick={() => setWeekStart((w) => w.clone().add(1, "week"))}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
              <div className="card-body p-2" style={{ overflowX: "auto" }}>
                <div className="d-flex gap-2" style={{ minWidth: 640 }}>
                  {weekDays.map((day) => {
                    const isToday = day.isSame(moment(), "day");
                    const dayStr  = day.format("YYYY-MM-DD");
                    const dayAppts = calendarAppts
                      .filter((apt) => {
                        if (!apt.appointment_date) return false;
                        return moment(apt.appointment_date).format("YYYY-MM-DD") === dayStr;
                      })
                      .sort((a, b) => {
                        if (!a.appointment_time) return 1;
                        if (!b.appointment_time) return -1;
                        return a.appointment_time.localeCompare(b.appointment_time);
                      });
                    return (
                      <div
                        key={dayStr}
                        className={`flex-fill rounded-3 overflow-hidden border ${
                          isToday ? "border-primary border-2 shadow-sm" : "border-secondary border-opacity-25"
                        }`}
                        style={{ minWidth: 88, flex: "1 1 0" }}
                      >
                        <div className={`text-center py-2 px-1 ${isToday ? "bg-primary text-white" : "bg-light text-secondary"}`}>
                          <div className="small fw-semibold text-uppercase" style={{ letterSpacing: 1 }}>
                            {day.format("ddd")}
                          </div>
                          <div className="fw-bold" style={{ fontSize: 22, lineHeight: 1.1 }}>
                            {day.format("D")}
                          </div>
                          <div className="small opacity-75">{day.format("MMM")}</div>
                          {dayAppts.length > 0 && (
                            <span
                              className={`badge rounded-pill mt-1 ${isToday ? "bg-white text-primary" : "bg-primary"}`}
                              style={{ fontSize: 10 }}
                            >
                              {dayAppts.length}
                            </span>
                          )}
                        </div>
                        <div className="p-1" style={{ minHeight: 90, maxHeight: 320, overflowY: "auto" }}>
                          {dayAppts.length === 0 ? (
                            <div className="text-center text-muted py-4" style={{ fontSize: 20 }}>&#8211;</div>
                          ) : (
                            dayAppts.map((apt) => {
                              const name =
                                apt.client_name ||
                                (apt.client ? `${apt.client.first_name} ${apt.client.last_name}` : "Client");
                              const trainer = apt.trainer
                                ? `${apt.trainer.first_name} ${apt.trainer.last_name}`
                                : null;
                              const color = conversionColor(apt.conversion);
                              const timeStr = apt.appointment_time
                                ? moment(String(apt.appointment_time), ["HH:mm:ss", "HH:mm"]).format("h:mm A")
                                : "";
                              return (
                                <div
                                  key={apt.id}
                                  className="rounded-2 mb-1 p-1"
                                  style={{ background: `${color}18`, borderLeft: `3px solid ${color}`, fontSize: 11, lineHeight: 1.4 }}
                                >
                                  {timeStr && (
                                    <div className="fw-bold" style={{ color, fontSize: 10 }}>
                                      <i className="bi bi-clock me-1"></i>{timeStr}
                                    </div>
                                  )}
                                  <div className="fw-semibold text-dark text-truncate" title={name}>{name}</div>
                                  {trainer && (
                                    <div className="text-muted text-truncate" title={trainer} style={{ fontSize: 10 }}>
                                      <i className="bi bi-person me-1"></i>{trainer}
                                    </div>
                                  )}
                                  {apt.conversion && (
                                    <span className="badge mt-1" style={{ background: color, fontSize: 9 }}>
                                      {apt.conversion}
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Feature Detail Cards — Diet Plans · Health Camps · Referrals */}
            <div className="row g-3 mb-4">

              {/* Diet Plans Detail */}
              <div className="col-12 col-xl-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 fw-semibold">
                      <i className="bi bi-journal-medical me-2 text-success"></i>Diet Plans Issued
                    </h6>
                    <a href="/nutrition/diet-plans" className="btn btn-sm btn-outline-success">
                      View All
                    </a>
                  </div>
                  <div className="card-body">
                    <div className="row g-2 mb-3">
                      {[
                        { label: "Total", value: dietStats?.total ?? "—", icon: "bi-stack", color: "#0d7a5f" },
                        { label: "This Week", value: dietStats?.this_week ?? "—", icon: "bi-calendar-week", color: "#198754" },
                        { label: "Issued", value: dietStats?.issued_count ?? "—", icon: "bi-check-circle-fill", color: "#20c997" },
                      ].map((s, i) => (
                        <div key={i} className="col-4">
                          <div className="text-center p-2 rounded-3" style={{ background: `${s.color}12` }}>
                            <i className={`bi ${s.icon} mb-1`} style={{ color: s.color, fontSize: 18 }}></i>
                            <div className="fw-bold" style={{ color: s.color }}>{s.value}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{s.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {dietStats?.goals && dietStats.goals.length > 0 && (
                      <>
                        <p className="small text-muted fw-semibold mb-2">Top Goals</p>
                        <div className="d-flex flex-column gap-1">
                          {dietStats.goals.map((g, i) => {
                            const total = dietStats.goal_total || dietStats.goals.reduce((s, x) => s + x.count, 0);
                            const pct = total > 0 ? Math.round((g.count / total) * 100) : 0;
                            const colors = ["#0d7a5f", "#198754", "#20c997", "#6fdc8c", "#d4edda", "#a8d5b5"];
                            return (
                              <div key={i} className="d-flex align-items-center gap-2">
                                <div className="flex-grow-1">
                                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: 12 }}>
                                    <span className="text-truncate">{g.goal}</span>
                                    <span className="fw-semibold">{g.count}</span>
                                  </div>
                                  <div className="progress" style={{ height: 5 }}>
                                    <div
                                      className="progress-bar"
                                      style={{ width: `${pct}%`, background: colors[i % colors.length] }}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Health Camps Detail */}
              <div className="col-12 col-xl-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 fw-semibold">
                      <i className="bi bi-hospital me-2 text-primary"></i>Health Camps
                    </h6>
                    <a href="/nutrition/health-camps" className="btn btn-sm btn-outline-primary">
                      View All
                    </a>
                  </div>
                  <div className="card-body">
                    <div className="row g-2 mb-3">
                      {[
                        { label: "Total", value: campStats?.total ?? "—", icon: "bi-people-fill", color: "#6610f2" },
                        { label: "This Week", value: campStats?.this_week ?? "—", icon: "bi-calendar-week", color: "#6f42c1" },
                        { label: "Upcoming", value: campStats?.upcoming ?? "—", icon: "bi-calendar-event", color: "#0d6efd" },
                      ].map((s, i) => (
                        <div key={i} className="col-4">
                          <div className="text-center p-2 rounded-3" style={{ background: `${s.color}12` }}>
                            <i className={`bi ${s.icon} mb-1`} style={{ color: s.color, fontSize: 18 }}></i>
                            <div className="fw-bold" style={{ color: s.color }}>{s.value}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{s.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {campStats?.conversions && campStats.conversions.length > 0 && (
                      <>
                        <p className="small text-muted fw-semibold mb-2">Consultation Types</p>
                        <div className="list-group list-group-flush">
                          {campStats.conversions.slice(0, 5).map((c, i) => (
                            <div key={i} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1 border-0">
                              <span className="text-truncate small me-2" title={c.conversion}>{c.conversion}</span>
                              <span className="badge rounded-pill" style={{ background: "#6610f220", color: "#6610f2", fontWeight: 600 }}>
                                {c.count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Referrals Detail */}
              <div className="col-12 col-xl-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 fw-semibold">
                      <i className="bi bi-person-plus-fill me-2 text-warning"></i>Referral Sheet
                    </h6>
                    <a href="/nutrition/referrals" className="btn btn-sm btn-outline-warning">
                      View All
                    </a>
                  </div>
                  <div className="card-body">
                    <div className="row g-2 mb-3">
                      {[
                        { label: "Total Referrals", value: referralStats?.total_referrals ?? "—", icon: "bi-person-plus", color: "#fd7e14" },
                        { label: "Transformations", value: referralStats?.total_transformations ?? "—", icon: "bi-award-fill", color: "#ffc107" },
                        { label: "Active Clients", value: referralStats?.total_active_clients ?? "—", icon: "bi-people", color: "#e67e00" },
                      ].map((s, i) => (
                        <div key={i} className="col-4">
                          <div className="text-center p-2 rounded-3" style={{ background: `${s.color}14` }}>
                            <i className={`bi ${s.icon} mb-1`} style={{ color: s.color, fontSize: 18 }}></i>
                            <div className="fw-bold" style={{ color: s.color }}>{s.value}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{s.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* This Week breakdown */}
                    <p className="small text-muted fw-semibold mb-2">This Week</p>
                    <div className="d-flex flex-column gap-2">
                      {[
                        { label: "Referrals", value: referralStats?.current_week?.referrals ?? 0, icon: "bi-person-plus", color: "#fd7e14" },
                        { label: "Transformations", value: referralStats?.current_week?.transformations ?? 0, icon: "bi-award", color: "#ffc107" },
                        { label: "Active Clients", value: referralStats?.current_week?.active_clients ?? 0, icon: "bi-people", color: "#e67e00" },
                      ].map((r, i) => (
                        <div key={i} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                          <span className="small">
                            <i className={`bi ${r.icon} me-1`} style={{ color: r.color }}></i>
                            {r.label}
                          </span>
                          <strong style={{ color: r.color }}>{r.value}</strong>
                        </div>
                      ))}
                    </div>
                    <div className="row g-2 mt-2">
                      <div className="col-6">
                        <div className="d-flex align-items-center gap-2 p-2 rounded-3" style={{ background: "#25d36614" }}>
                          <i className="bi bi-google" style={{ color: "#25d366" }}></i>
                          <div>
                            <div className="fw-bold small" style={{ color: "#25d366" }}>{referralStats?.total_google_reviews ?? "—"}</div>
                            <div className="text-muted" style={{ fontSize: 10 }}>Google Reviews</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="d-flex align-items-center gap-2 p-2 rounded-3" style={{ background: "#e3126914" }}>
                          <i className="bi bi-camera-video-fill" style={{ color: "#e31269" }}></i>
                          <div>
                            <div className="fw-bold small" style={{ color: "#e31269" }}>{referralStats?.total_video_shoots ?? "—"}</div>
                            <div className="text-muted" style={{ fontSize: 10 }}>Video Shoots</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Conversion Stats + Quick Actions */}
            <div className="row g-3">
              <div className="col-12 col-xl-5">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-bottom">
                    <h5 className="mb-0">
                      <i className="bi bi-bar-chart-fill me-2"></i>Conversion Stats
                    </h5>
                  </div>
                  <div className="card-body">
                    {!statistics?.conversion_stats || statistics.conversion_stats.length === 0 ? (
                      <p className="text-muted text-center py-3">No data available</p>
                    ) : (
                      <div className="list-group list-group-flush">
                        {statistics.conversion_stats.slice(0, 10).map((stat, i) => (
                          <div key={i} className="list-group-item d-flex justify-content-between align-items-center px-0">
                            <span>{stat.conversion || "Unknown"}</span>
                            <span className="badge bg-primary rounded-pill">{stat.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-7">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-bottom">
                    <h5 className="mb-0">
                      <i className="bi bi-lightning-fill me-2"></i>Quick Actions
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-6">
                        <Link to="/nutrition/appointments" className="btn btn-outline-primary w-100">
                          <i className="bi bi-calendar-plus me-2"></i>New Appointment
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link to="/nutrition/appointments" className="btn btn-outline-success w-100">
                          <i className="bi bi-list-check me-2"></i>All Appointments
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link to="/nutrition/diet-plans" className="btn btn-outline-secondary w-100">
                          <i className="bi bi-journal-medical me-2"></i>Diet Plans
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link to="/nutrition/health-camps" className="btn btn-outline-secondary w-100">
                          <i className="bi bi-hospital me-2"></i>Health Camps
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link to="/nutrition/referrals" className="btn btn-outline-warning w-100">
                          <i className="bi bi-person-plus me-2"></i>Referral Sheet
                        </Link>
                      </div>
                      <div className="col-6">
                        <button className="btn btn-outline-secondary w-100" onClick={() => { fetchStatistics(); fetchCalendarAppointments(); }}>
                          <i className="bi bi-arrow-clockwise me-2"></i>Refresh
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NutritionDashboard;
